/**
 * AptSync / ApartmentSync "View Details" page.
 *
 * The vendor script looks for this exact container and fills it with the
 * floor-plan details widget:
 *   <div class="rrac_floorplanDetails"></div>
 *
 * Script loading joins the site-wide ApartmentSync loader when it is already
 * on the page, and self-loads when it is not, so a client-side visit still
 * re-scans the new container.
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useContentToken } from '@vibesites/runtime/react';
import { useRRACScript } from '@vibesites/blocks/hooks/useRRACScript';

export function FloorplanDetails() {
  const javascriptId = useContentToken('floorplandetails.javascript_id');
  const configId = useContentToken('floorplandetails.config_id');

  useRRACScript({
    javascriptId: javascriptId || null,
    configId: configId || null,
    role: 'consumer',
  });

  return (
    <section className="floorplan-details bg-background">
      <div className="floorplan-details__meta" aria-hidden="true">
        <EditableToken path="floorplandetails.javascript_id" as="span">
          {javascriptId}
        </EditableToken>
        <EditableToken path="floorplandetails.config_id" as="span">
          {configId}
        </EditableToken>
      </div>
      <div className="rrac_floorplanDetails" />

      <style>{`
        .floorplan-details {
          width: 100%;
          min-height: 400px;
        }
        .floorplan-details__meta {
          height: 0;
          overflow: hidden;
        }
        .floorplan-details .rrac_floorplanDetails {
          width: 100%;
          min-height: 400px;
          background: #ffffff;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplanDetails',
  displayName: 'ApartmentSync Floor Plan Details',
  tokens: {
    'floorplandetails.javascript_id': {
      label: 'JavaScript ID',
      type: 'text',
      defaultField: 'custom_apartmentsync_javascript_id',
      description:
        'Encoded token for the ApartmentSync script URL. Found in the Repli360 admin dashboard.',
    },
    'floorplandetails.config_id': {
      label: 'Config ID (Site ID)',
      type: 'text',
      defaultField: 'custom_apartmentsync_config_id',
      description:
        'ApartmentSync site identifier. Baked into the loaded script; stored here for reference.',
    },
  },
};

(FloorplanDetails as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplanDetails;
